import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { API_URL as SHARED_API_URL } from '../../utils/apiUrl';
import { Capacitor } from '@capacitor/core';
import { openExternalUrl } from '../../utils/externalUrl';
import {
  EyeIcon,
  EyeSlashIcon,
  BoltIcon,
  EnvelopeIcon,
  LockClosedIcon,
  NewspaperIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { login, selectIsLoading, selectAuthError, clearError, selectIsAuthenticated, selectUserRole } from '../../store/slices/authSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { isBiometricAvailable, isBiometricEnabled, getBiometricCredentials, getBiometricLabel, hasSavedCredentials, clearBiometricData } from '../../utils/biometricAuth';
import ProductSlider from '../../components/shop/ProductSlider';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import { fetchPublicProducts, selectProducts } from '../../store/slices/shopSlice';
import { blogAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

// Instagram Icon Component
const InstagramIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const API_URL = SHARED_API_URL;

const LoginPage = () => {
  const { t } = useTranslation(['auth', 'shop', 'blog']);
  const { theme, getGradient } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoading = useSelector(selectIsLoading);
  const error = useSelector(selectAuthError);
  const products = useSelector(selectProducts);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userRole = useSelector(selectUserRole);
  const [showPassword, setShowPassword] = useState(false);
  const [logo, setLogo] = useState(null);
  const [instagramUrl, setInstagramUrl] = useState('');
  const [appStoreUrl, setAppStoreUrl] = useState('');
  const [playStoreUrl, setPlayStoreUrl] = useState('');
  const [biometricType, setBiometricType] = useState(null); // 'faceId', 'touchId', or null
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [latestPost, setLatestPost] = useState(null);

  // Get redirect URL from query params (e.g., /login?redirect=/shop/product/123)
  const redirectUrl = searchParams.get('redirect');
  const autoEmail = searchParams.get('email');
  const autoPass = searchParams.get('pass');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm();

  // Auto-login when email and password are provided via URL (from welcome email)
  useEffect(() => {
    if (autoEmail && autoPass) {
      setValue('email', autoEmail);
      setValue('password', autoPass);
      dispatch(login({ email: autoEmail, password: autoPass }));
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Fetch logo from public endpoint (no auth required)
    fetch(`${API_URL}/public/logo`)
      .then(res => res.json())
      .then(data => setLogo(data.logoUrl))
      .catch(err => console.error('Error fetching logo:', err));

    // Fetch products
    dispatch(fetchPublicProducts())
      .then((result) => {
        if (result.type.endsWith('/rejected')) {
          console.error('Failed to fetch public products:', result.payload);
        }
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
      });

    // Fetch social links via public route (no auth required)
    fetch(`${API_URL}/public/social-links`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setInstagramUrl(data.instagramUrl || '');
          setAppStoreUrl(data.appStoreUrl || '');
          setPlayStoreUrl(data.playStoreUrl || '');
        }
      })
      .catch(() => {});

    // Fetch the latest public blog post (non-blocking) for the banner card.
    blogAPI.getPublicPosts()
      .then(d => setLatestPost((d.data?.posts || [])[0] || null))
      .catch(() => {});

    // Check if biometric login is available, enabled, AND has saved credentials
    let bioAborted = false;
    if (Capacitor.isNativePlatform()) {
      (async () => {
        if (bioAborted) return;
        try {
          const { isAvailable, biometryType } = await isBiometricAvailable();
          if (bioAborted) return;
          if (isAvailable && isBiometricEnabled()) {
            const hasCredentials = await hasSavedCredentials();
            if (!bioAborted && hasCredentials) {
              setBiometricType(biometryType);
            }
          }
        } catch (err) {
          // Ignore
        }
      })();
      return () => { bioAborted = true; };
    }
  }, [dispatch]);

  // Handle redirect after successful login
  useEffect(() => {
    if (isAuthenticated && userRole) {
      // If there's a redirect URL (e.g., from public shop), go there
      if (redirectUrl) {
        navigate(decodeURIComponent(redirectUrl), { replace: true });
        return;
      }
      
      // Otherwise, let the default routing in App.js handle dashboard redirect
      // (no action needed here - App.js will redirect based on role)
    }
  }, [isAuthenticated, userRole, redirectUrl, navigate]);

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    dispatch(clearError());
    try {
      const { email, password } = await getBiometricCredentials();
      dispatch(login({ email, password }));
    } catch (err) {
      if (err?.message !== 'User cancelled' && err?.code !== 'biometric_cancelled') {
        // Credentials may be stale (password changed) -- clear biometric data
        try { await clearBiometricData(); } catch {}
        setBiometricType(null);
        console.warn('Biometric login failed, cleared credentials:', err);
      } else {
        console.warn('Biometric login cancelled');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const onSubmit = (data) => {
    dispatch(clearError());
    dispatch(login(data));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Helmet>
        <title>ProTrener - Personalizirani Fitness Planovi</title>
        <meta name="description" content="Personalizirani fitness planovi od vrhunskih trenera. Povežite se sa trenerom, pratite treninge i postignite svoje ciljeve." />
        <link rel="canonical" href="https://protrener.app/login" />
        <meta property="og:title" content="ProTrener - Personalizirani Fitness Planovi" />
        <meta property="og:description" content="Personalizirani fitness planovi od vrhunskih trenera. Povežite se sa trenerom, pratite treninge i postignite svoje ciljeve." />
        <meta property="og:image" content="https://protrener.app/favicon.ico" />
        <meta property="og:url" content="https://protrener.app/login" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="hr_HR" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="ProTrener - Personalizirani Fitness Planovi" />
        <meta name="twitter:description" content="Personalizirani fitness planovi od vrhunskih trenera." />
      </Helmet>
      {/* Hero Header */}
      <div className="relative">
        {/* Image container - clips the image only */}
        <div className={`h-32 md:h-40 bg-gradient-to-r ${getGradient(0)} overflow-hidden`}>
          {logo ? (
            <img 
              src={logo} 
              alt="App Logo" 
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg">
                <BoltIcon className="h-8 w-8 md:h-10 md:w-10 text-white" />
              </div>
            </div>
          )}
        </div>

        {/* Social & Store Links - On the hero image, bottom-left */}
        {(instagramUrl || (!Capacitor.isNativePlatform() && (appStoreUrl || playStoreUrl))) && (
          <div className="absolute bottom-3 left-4 z-20 flex items-center gap-2">
            {instagramUrl && (
              Capacitor.isNativePlatform() ? (
                <button
                  onClick={() => openExternalUrl(instagramUrl)}
                  className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-md active:scale-95 transition-all"
                  title="Instagram"
                >
                  <InstagramIcon className="h-5 w-5 text-pink-600" />
                </button>
              ) : (
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-md hover:shadow-lg transition-all active:scale-95"
                  title="Instagram"
                >
                  <InstagramIcon className="h-5 w-5 text-pink-600" />
                </a>
              )
            )}
            {!Capacitor.isNativePlatform() && appStoreUrl && (
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-md hover:shadow-lg transition-all active:scale-95"
                title="App Store"
              >
                <svg className="h-5 w-5 text-gray-800" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </a>
            )}
            {!Capacitor.isNativePlatform() && playStoreUrl && (
              <a
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow-md hover:shadow-lg transition-all active:scale-95"
                title="Google Play"
              >
                <svg className="h-5 w-5 text-gray-800" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 010 1.732l-2.807 1.626L15.206 12l2.492-2.492zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
              </a>
            )}
          </div>
        )}

        {/* Language Switcher - On the hero image, bottom-right */}
        <div className="absolute bottom-3 right-4 z-20">
          <LanguageSwitcher className="bg-white/90 backdrop-blur-sm shadow-md rounded-lg" />
        </div>
      </div>

      {/* Product Slider Section */}
      {products && products.length > 0 && (
        <div className="py-6 md:py-8 bg-white/50 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">
              🏋️ {t('shop:landing.trainingPrograms')}
            </h2>
            <ProductSlider products={products} title="" compact={true} showCount={false} />
            
            {/* Products Count and View All Link */}
            <div className="flex items-center justify-between mt-3 px-1">
              <span className="text-xs text-gray-400">
                {t('shop:landing.programsAvailable', { count: products.length })}
              </span>
              <Link 
                to="/shop/browse"
                className={`text-xs ${theme.sectionLink} transition-colors`}
              >
                {t('shop:landing.viewAllOffers', 'View all offers')} →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Blog Banner — shows the latest published article, or a fallback banner */}
      <div className="px-4 pt-4">
        <div className="max-w-5xl mx-auto">
          {latestPost ? (
            <Link
              to="/blog"
              className="group flex items-center gap-3 md:gap-4 bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-3 md:p-4 hover:shadow-xl transition-all active:scale-[0.99]"
            >
              {/* Cover thumbnail (gradient fallback if none) */}
              {latestPost.coverImageUrl ? (
                <img
                  src={latestPost.coverImageUrl}
                  alt=""
                  className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover"
                  loading="lazy"
                />
              ) : (
                <div className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl bg-gradient-to-br ${getGradient(0)} flex items-center justify-center`}>
                  <NewspaperIcon className="h-7 w-7 md:h-8 md:w-8 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <span className={`text-[11px] md:text-xs font-bold uppercase tracking-wide ${theme.sectionLink}`}>
                  {t('blog:latestLabel')}
                </span>
                <h3 className="text-sm md:text-base font-bold text-gray-900 leading-tight line-clamp-2">
                  {latestPost.title}
                </h3>
                {latestPost.excerpt && (
                  <p className="text-xs md:text-sm text-gray-500 line-clamp-1 mt-0.5">
                    {latestPost.excerpt}
                  </p>
                )}
              </div>
              <div className="flex-shrink-0 flex items-center gap-1 text-gray-400 group-hover:text-gray-600 transition-colors self-center">
                <span className="hidden sm:inline text-xs font-semibold">Blog</span>
                <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ) : (
            <Link
              to="/blog"
              className={`group flex items-center gap-4 bg-gradient-to-r ${getGradient(0)} rounded-2xl shadow-lg p-4 md:p-5 text-white hover:opacity-95 transition-all active:scale-[0.99]`}
            >
              <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <NewspaperIcon className="h-6 w-6 md:h-7 md:w-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base md:text-lg font-bold leading-tight">
                  {t('blog:bannerTitle')}
                </h3>
                <p className="text-xs md:text-sm text-white/90 line-clamp-2">
                  {t('blog:bannerSubtitle')}
                </p>
              </div>
              <ArrowRightIcon className="h-5 w-5 md:h-6 md:w-6 text-white/90 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          )}
        </div>
      </div>

      {/* Login Form Section */}
      <div className="flex items-center justify-center py-6 px-4">
        <div className="max-w-md w-full">
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden">
            {/* Form Content */}
            <div className="p-6">
              <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                    📧 {t('login.emailLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      autoComplete="username"
                      name="email"
                      className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                        errors.email 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-200'
                      }`}
                      placeholder={t('login.emailPlaceholder')}
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address'
                        }
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-1.5 text-xs text-red-600 font-semibold">
                      ⚠️ {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-2">
                    🔒 {t('login.passwordLabel')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                        errors.password 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-200'
                      }`}
                      placeholder={t('login.passwordPlaceholder')}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-50 rounded-r-xl transition-colors active:scale-95"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1.5 text-xs text-red-600 font-semibold">
                      ⚠️ {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-red-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-semibold text-red-800">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full bg-gradient-to-r ${getGradient(0)} text-white py-3.5 rounded-xl font-bold text-lg shadow-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 flex items-center justify-center`}
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" color="white" className="mr-2" />
                      {t('login.loggingIn')}
                    </>
                  ) : (
                    <>
                      <BoltIcon className="h-5 w-5 mr-2" />
                      {t('login.submitButton')}
                    </>
                  )}
                </button>

                {/* Biometric Login Button */}
                {biometricType && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-3 text-gray-400">or</span>
                    </div>
                  </div>
                )}
                {biometricType && (
                  <button
                    type="button"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading || isLoading}
                    className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-bold text-lg shadow-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 flex items-center justify-center"
                  >
                    {biometricLoading ? (
                      <>
                        <LoadingSpinner size="sm" color="white" className="mr-2" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        {biometricType === 'faceId' ? (
                          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 11.75a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm6 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-7.5 3a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1-.75-.75zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.81 4.47c-.08 0-.16-.02-.23-.06C15.66 3.42 14 3 12.01 3c-1.98 0-3.86.47-5.57 1.41-.24.13-.54.04-.68-.2a.506.506 0 0 1 .2-.68C7.82 2.52 9.86 2 12.01 2c2.13 0 3.99.47 6.03 1.52.25.13.34.43.21.67a.49.49 0 0 1-.44.28zM3.5 9.72a.499.499 0 0 1-.41-.79c.99-1.4 2.25-2.5 3.75-3.27C9.98 4.04 14 4.03 17.15 5.65c1.5.77 2.76 1.86 3.75 3.25a.5.5 0 0 1-.12.7.5.5 0 0 1-.7-.12 9.388 9.388 0 0 0-3.39-2.94c-2.87-1.47-6.54-1.47-9.4.01-1.36.7-2.5 1.7-3.4 2.96-.08.14-.23.21-.39.21zm6.25 12.07a.47.47 0 0 1-.35-.15c-.87-.87-1.34-1.43-2.01-2.64-.69-1.23-1.05-2.73-1.05-4.34 0-2.97 2.54-5.39 5.66-5.39s5.66 2.42 5.66 5.39c0 .28-.22.5-.5.5s-.5-.22-.5-.5c0-2.42-2.09-4.39-4.66-4.39-2.57 0-4.66 1.97-4.66 4.39 0 1.44.32 2.77.93 3.85.64 1.15 1.08 1.64 1.85 2.42.19.2.19.51 0 .71-.11.1-.24.15-.37.15zm7.17-1.85c-1.19 0-2.24-.3-3.1-.89-1.49-1.01-2.38-2.65-2.38-4.39 0-.28.22-.5.5-.5s.5.22.5.5c0 1.41.72 2.74 1.94 3.56.71.48 1.54.72 2.54.72.26 0 .45.11.5.5a.5.5 0 0 1-.5.5zm-4.31-2.02c-.49 0-.27-.01-.74-.03a.507.507 0 0 1-.48-.52c.01-.28.23-.49.51-.48.39.02.58.03.71.03 1.03 0 1.87-.84 1.87-1.87 0-.28.22-.5.5-.5s.5.22.5.5a2.88 2.88 0 0 1-2.87 2.87z" />
                          </svg>
                        )}
                        {t('login.biometricLogin', `Sign in with ${getBiometricLabel(biometricType)}`)}
                      </>
                    )}
                  </button>
                )}

                {/* Sign Up Link */}
                <div className="text-center pt-2">
                  <span className="text-sm text-gray-600">
                    {t('login.noAccount')}{' '}
                  </span>
                  <Link
                    to="/register"
                    className={`text-sm font-bold ${theme.sectionLink} transition-colors`}
                  >
                    {t('login.registerMember')} →
                  </Link>
                </div>
              </form>
            </div>
          </div>

          {/* Footer Note with Links */}
          <p className="text-center text-xs text-gray-500 mt-6">
            {t('login.agreement', 'By signing in, you agree to our')}{' '}
            <Link to="/terms" className={`${theme.sectionLink} font-semibold`}>
              {t('login.termsOfService', 'Terms of Service')}
            </Link>
            {' '}{t('login.and', 'and')}{' '}
            <Link to="/privacy" className={`${theme.sectionLink} font-semibold`}>
              {t('login.privacyPolicy', 'Privacy Policy')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
